import type { TextTemplateCategory, TextTemplateItem, TextTemplateStyle } from './textTemplateCatalog';

const marketingBanner = (
  id: string,
  label: string,
  fontFamily: string,
  fontSize: number,
  width: number,
  extra: TextTemplateStyle & {
    themeAdaptiveFill?: boolean;
    previewScale?: number;
    keywords?: string[];
  } = {},
): TextTemplateItem => {
  const { themeAdaptiveFill, previewScale, keywords, ...styleExtra } = extra;
  return {
    id,
    label,
    themeAdaptiveFill,
    previewScale: previewScale ?? (fontSize >= 56 ? 2.25 : 2),
    keywords: ['banner', 'marketing', 'promo', label, ...(keywords ?? [])],
    style: {
      text: label,
      fontFamily,
      fontSize,
      width,
      height: Math.round(fontSize * 1.15),
      align: 'center',
      letterSpacing: 0,
      lineHeight: 1.05,
      x: Math.max(50, Math.round((520 - width) / 2)),
      y: 95,
      ...styleExtra,
    },
  };
};

const retailTag = (
  id: string,
  label: string,
  fontFamily: string,
  fontSize: number,
  width: number,
  extra: TextTemplateStyle & {
    themeAdaptiveFill?: boolean;
    previewScale?: number;
    keywords?: string[];
  } = {},
): TextTemplateItem =>
  marketingBanner(id, label, fontFamily, fontSize, width, {
    fontStyle: 'bold',
    letterSpacing: fontSize <= 22 ? 2.5 : 1.5,
    previewScale: extra.previewScale ?? (fontSize <= 24 ? 2.65 : 2.35),
    keywords: ['tag', 'label', 'badge', ...(extra.keywords ?? [])],
    ...extra,
  });

/** Additional ready-made banner presets (marketing / promo focus). */
export const EXTRA_TEXT_TEMPLATE_CATEGORIES: TextTemplateCategory[] = [
  {
    id: 'seasonal-holidays',
    title: 'Seasonal & holidays',
    description: 'Festive and calendar moments for retail banners',
    templates: [
      marketingBanner('xmas-sale', 'CHRISTMAS SALE', 'Bebas Neue', 56, 380, {
        fill: '#dc2626',
        letterSpacing: 2,
        keywords: ['christmas', 'winter', 'holiday'],
      }),
      marketingBanner('ny-sale', 'NEW YEAR SALE', 'Anton', 52, 400, {
        fill: '#fbbf24',
        letterSpacing: 2,
        keywords: ['new year', 'january'],
      }),
      marketingBanner('valentine', "VALENTINE'S DAY ❤️", 'Playfair Display', 40, 440, {
        fill: '#f8558e',
        fontStyle: 'italic',
        previewScale: 1.8,
      }),
      marketingBanner('mothers-day', "MOTHER'S DAY", 'Dancing Script', 44, 360, {
        fill: '#ec4899',
        previewScale: 1.75,
      }),
      marketingBanner('fathers-day', "FATHER'S DAY", 'Oswald', 46, 360, {
        fill: '#2563eb',
        letterSpacing: 2,
      }),
      marketingBanner('easter', 'EASTER SPECIAL', 'Lilita One', 48, 360, {
        fill: '#a855f7',
      }),
      marketingBanner('diwali', 'DIWALI OFFERS', 'Bebas Neue', 54, 360, {
        fill: '#f59e0b',
        letterSpacing: 2,
        keywords: ['festival', 'india'],
      }),
      marketingBanner('eid', 'EID MUBARAK', 'GreatVibes', 46, 320, {
        fill: '#22c55e',
        previewScale: 1.8,
      }),
      marketingBanner('halloween', 'HALLOWEEN SALE', 'Anton', 52, 380, {
        fill: '#f97316',
        letterSpacing: 2,
      }),
      marketingBanner('thanksgiving', 'THANKSGIVING SALE', 'Barlow Condensed', 44, 400, {
        fontStyle: 'bold',
        fill: '#ea580c',
      }),
      marketingBanner('back-to-school', 'BACK TO SCHOOL', 'Montserrat', 42, 400, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        letterSpacing: 1,
      }),
      marketingBanner('summer-sale', 'SUMMER SALE', 'Bebas Neue', 58, 360, {
        fill: '#06b6d4',
        letterSpacing: 3,
      }),
    ],
  },
  {
    id: 'ecommerce-shop',
    title: 'E‑commerce & shop',
    description: 'Store CTAs, cart, checkout, and shipping lines',
    templates: [
      marketingBanner('add-to-cart', 'ADD TO CART', 'Bebas Neue', 50, 320, {
        fill: '#ffffff',
        letterSpacing: 2,
        keywords: ['cta', 'shop'],
      }),
      marketingBanner('buy-now', 'BUY NOW', 'Anton', 56, 260, {
        fill: '#22c55e',
        letterSpacing: 2,
      }),
      marketingBanner('checkout', 'CHECKOUT NOW', 'Archivo Black', 46, 340, {
        fill: '#f8558e',
      }),
      marketingBanner('pay-now', 'PAY NOW', 'League Spartan', 48, 280, {
        fontStyle: 'bold',
        fill: '#6366f1',
      }),
      marketingBanner('free-returns', 'FREE RETURNS', 'Roboto Condensed', 38, 340, {
        fontStyle: 'bold',
        fill: '#14b8a6',
      }),
      marketingBanner('easy-returns', '30-DAY RETURNS', 'Barlow Condensed', 34, 380, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.8,
      }),
      marketingBanner('cod', 'CASH ON DELIVERY', 'Montserrat', 36, 400, {
        fontStyle: 'bold',
        fill: '#fbbf24',
        previewScale: 1.75,
      }),
      marketingBanner('same-day', 'SAME DAY DELIVERY', 'FiraSansCondensed', 36, 400, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        previewScale: 1.75,
      }),
      marketingBanner('track-order', 'TRACK YOUR ORDER', 'Inter', 28, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.65,
      }),
      marketingBanner('wishlist', 'ADD TO WISHLIST', 'Outfit', 32, 380, {
        fontStyle: 'bold',
        fill: '#f8558e',
        previewScale: 1.7,
      }),
      marketingBanner('gift-card', 'GIFT CARDS', 'Bebas Neue', 52, 300, {
        fill: '#a855f7',
        letterSpacing: 2,
      }),
      marketingBanner('store-locator', 'FIND A STORE', 'Oswald', 44, 320, {
        letterSpacing: 3,
        fill: '#ffffff',
      }),
      marketingBanner('compare-price', 'WAS $99 NOW $49', 'Roboto Condensed', 28, 420, {
        fontStyle: 'bold',
        fill: '#ef4444',
        previewScale: 1.6,
        keywords: ['pricing', 'strike'],
      }),
      marketingBanner('secure-pay', 'SECURE CHECKOUT', 'Montserrat', 34, 380, {
        fontStyle: 'bold',
        fill: '#22c55e',
        previewScale: 1.7,
      }),
    ],
  },
  {
    id: 'urgency-fomo',
    title: 'Urgency & FOMO',
    description: 'Scarcity, countdown, and “act now” messaging',
    templates: [
      marketingBanner('hurry', 'HURRY!', 'Anton', 58, 220, {
        fill: '#ef4444',
        letterSpacing: 2,
      }),
      marketingBanner('dont-miss', "DON'T MISS OUT", 'Bebas Neue', 50, 380, {
        fill: '#f97316',
        letterSpacing: 2,
      }),
      marketingBanner('almost-gone', 'ALMOST GONE', 'Archivo Black', 48, 360, {
        fill: '#ffffff',
        stroke: '#ef4444',
        strokeWidth: 2,
      }),
      marketingBanner('only-left', 'ONLY 3 LEFT', 'League Spartan', 44, 320, {
        fontStyle: 'bold',
        fill: '#fbbf24',
      }),
      marketingBanner('selling-fast', 'SELLING FAST', 'Barlow Condensed', 42, 340, {
        fontStyle: 'bold',
        fill: '#f8558e',
      }),
      marketingBanner('ends-midnight', 'ENDS AT MIDNIGHT', 'Montserrat', 36, 400, {
        fontStyle: 'bold',
        fill: '#ffffff',
        previewScale: 1.75,
      }),
      marketingBanner('24h-only', '24 HOURS ONLY', 'Bebas Neue', 52, 360, {
        fill: '#dc2626',
        letterSpacing: 2,
      }),
      marketingBanner('while-stocks', 'WHILE STOCKS LAST', 'Roboto Condensed', 32, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.7,
      }),
      marketingBanner('one-day-only', 'ONE DAY ONLY', 'Anton', 50, 340, {
        fill: '#7c3aed',
        letterSpacing: 2,
      }),
      marketingBanner('act-now', 'ACT NOW', 'Oswald', 54, 280, {
        letterSpacing: 4,
        fill: '#ef4444',
      }),
      marketingBanner('running-out', 'RUNNING OUT', 'Lilita One', 46, 340, {
        fill: '#fb923c',
      }),
      marketingBanner('final-hours', 'FINAL HOURS', 'Archivo Black', 48, 360, {
        fill: '#ffffff',
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        shadowBlur: 16,
        shadowColor: '#ef4444',
        shadowOpacity: 0.9,
      }),
    ],
  },
  {
    id: 'pricing-value',
    title: 'Pricing & value',
    description: 'Starting prices, bundles, and value props',
    templates: [
      marketingBanner('starting-at', 'STARTING AT $9.99', 'Bebas Neue', 48, 400, {
        fill: '#22c55e',
        letterSpacing: 1,
        previewScale: 2,
      }),
      marketingBanner('from-price', 'FROM $19', 'Anton', 52, 280, {
        fill: '#fbbf24',
      }),
      marketingBanner('under-price', 'UNDER $50', 'Archivo Black', 50, 300, {
        fill: '#ffffff',
      }),
      marketingBanner('best-deal', 'BEST DEAL', 'Bebas Neue', 54, 300, {
        fill: '#f8558e',
        letterSpacing: 3,
      }),
      marketingBanner('value-pack', 'VALUE PACK', 'Barlow Condensed', 44, 320, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
      }),
      marketingBanner('2-for', '2 FOR $25', 'League Spartan', 48, 280, {
        fontStyle: 'bold',
        fill: '#a855f7',
      }),
      marketingBanner('no-hidden-fees', 'NO HIDDEN FEES', 'Roboto Condensed', 30, 380, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.7,
      }),
      marketingBanner('price-match', 'PRICE MATCH', 'Montserrat', 40, 320, {
        fontStyle: 'bold',
        fill: '#16a34a',
      }),
      marketingBanner('financing', '0% FINANCING', 'Oswald', 46, 320, {
        letterSpacing: 2,
        fill: '#ffffff',
      }),
      marketingBanner('free-trial', 'FREE TRIAL', 'Outfit', 44, 300, {
        fontStyle: 'bold',
        fill: '#6366f1',
      }),
    ],
  },
  {
    id: 'food-restaurant',
    title: 'Food & restaurant',
    description: 'Menus, delivery, and dining promos',
    templates: [
      marketingBanner('order-food', 'ORDER FOOD', 'Bebas Neue', 54, 300, {
        fill: '#ef4444',
        letterSpacing: 2,
      }),
      marketingBanner('free-delivery-food', 'FREE DELIVERY', 'Anton', 48, 360, {
        fill: '#22c55e',
      }),
      marketingBanner('todays-special', "TODAY'S SPECIAL", 'Playfair Display', 38, 400, {
        fontStyle: 'italic',
        fill: '#fbbf24',
        previewScale: 1.8,
      }),
      marketingBanner('fresh-made', 'FRESH MADE DAILY', 'Pacifico', 36, 400, {
        fill: '#14b8a6',
        previewScale: 1.7,
      }),
      marketingBanner('happy-hour', 'HAPPY HOUR', 'Lilita One', 50, 320, {
        fill: '#f97316',
      }),
      marketingBanner('brunch', 'BRUNCH MENU', 'Dancing Script', 42, 340, {
        fill: '#ec4899',
        previewScale: 1.75,
      }),
      marketingBanner('takeaway', 'TAKEAWAY', 'Bebas Neue', 56, 280, {
        fill: '#ffffff',
        letterSpacing: 3,
      }),
      marketingBanner('dine-in', 'DINE IN', 'Archivo Black', 52, 240, {
        fill: '#f8558e',
      }),
      marketingBanner('chef-special', 'CHEF SPECIAL', 'Cormorant Garamond', 40, 360, {
        fontStyle: 'italic',
        fill: '#fcd34d',
        previewScale: 1.8,
      }),
      marketingBanner('organic-menu', '100% ORGANIC', 'Montserrat', 38, 340, {
        fontStyle: 'bold',
        fill: '#15803d',
      }),
    ],
  },
  {
    id: 'services-local',
    title: 'Services & local',
    description: 'Calls, bookings, and local business banners',
    templates: [
      marketingBanner('call-now', 'CALL NOW', 'Anton', 54, 280, {
        fill: '#2563eb',
        letterSpacing: 2,
      }),
      marketingBanner('book-appointment', 'BOOK APPOINTMENT', 'Barlow Condensed', 38, 400, {
        fontStyle: 'bold',
        fill: '#14b8a6',
        previewScale: 1.75,
      }),
      marketingBanner('free-quote', 'FREE QUOTE', 'Bebas Neue', 52, 300, {
        fill: '#f8558e',
        letterSpacing: 2,
      }),
      marketingBanner('open-now', 'OPEN NOW', 'Archivo Black', 50, 260, {
        fill: '#22c55e',
      }),
      marketingBanner('we-come-to-you', 'WE COME TO YOU', 'Roboto Condensed', 34, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.7,
      }),
      marketingBanner('licensed-insured', 'LICENSED & INSURED', 'Roboto Condensed', 28, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.6,
        keywords: ['trust'],
      }),
      marketingBanner('24-7', '24/7 SERVICE', 'Oswald', 48, 300, {
        letterSpacing: 2,
        fill: '#ffffff',
      }),
      marketingBanner('visit-us', 'VISIT US TODAY', 'Montserrat', 36, 380, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        previewScale: 1.75,
      }),
      marketingBanner('schedule', 'SCHEDULE ONLINE', 'Inter', 30, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.65,
      }),
      marketingBanner('walk-ins', 'WALK-INS WELCOME', 'FiraSansCondensed', 34, 400, {
        fontStyle: 'bold',
        fill: '#f97316',
        previewScale: 1.7,
      }),
    ],
  },
  {
    id: 'events-webinar',
    title: 'Events & webinars',
    description: 'Registrations, launches, and live sessions',
    templates: [
      marketingBanner('register-now', 'REGISTER NOW', 'Bebas Neue', 52, 360, {
        fill: '#6366f1',
        letterSpacing: 2,
      }),
      marketingBanner('rsvp', 'RSVP TODAY', 'Anton', 50, 300, {
        fill: '#f8558e',
      }),
      marketingBanner('live-now', 'LIVE NOW', 'Archivo Black', 54, 280, {
        fill: '#ef4444',
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        shadowBlur: 14,
        shadowColor: '#ef4444',
        shadowOpacity: 0.85,
      }),
      marketingBanner('webinar', 'FREE WEBINAR', 'Montserrat', 40, 360, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
      }),
      marketingBanner('workshop', 'JOIN THE WORKSHOP', 'Barlow Condensed', 36, 400, {
        fontStyle: 'bold',
        fill: '#a855f7',
        previewScale: 1.75,
      }),
      marketingBanner('conference', 'ANNUAL CONFERENCE', 'Oswald', 44, 400, {
        letterSpacing: 3,
        fill: '#ffffff',
      }),
      marketingBanner('save-seat', 'SAVE YOUR SEAT', 'League Spartan', 40, 360, {
        fontStyle: 'bold',
        fill: '#fbbf24',
        previewScale: 1.8,
      }),
      marketingBanner('early-bird', 'EARLY BIRD', 'Bebas Neue', 54, 300, {
        fill: '#22c55e',
        letterSpacing: 3,
      }),
      marketingBanner('networking', 'NETWORKING NIGHT', 'Plus Jakarta Sans', 34, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.7,
      }),
      marketingBanner('product-demo', 'BOOK A DEMO', 'Outfit', 40, 300, {
        fontStyle: 'bold',
        fill: '#f8558e',
      }),
    ],
  },
  {
    id: 'loyalty-referral',
    title: 'Loyalty & referral',
    description: 'Rewards, VIP, and refer-a-friend',
    templates: [
      marketingBanner('rewards', 'EARN REWARDS', 'Montserrat', 40, 340, {
        fontStyle: 'bold',
        fill: '#fbbf24',
      }),
      marketingBanner('vip', 'VIP ACCESS', 'Bebas Neue', 54, 300, {
        fill: '#a855f7',
        letterSpacing: 3,
      }),
      marketingBanner('points', 'DOUBLE POINTS', 'Anton', 50, 340, {
        fill: '#22c55e',
      }),
      marketingBanner('refer-friend', 'REFER A FRIEND', 'Barlow Condensed', 38, 380, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        previewScale: 1.75,
      }),
      marketingBanner('get-10', 'GET $10 OFF', 'Archivo Black', 46, 320, {
        fill: '#f8558e',
      }),
      marketingBanner('loyalty', 'LOYALTY CLUB', 'Oswald', 46, 340, {
        letterSpacing: 2,
        fill: '#ffffff',
      }),
      marketingBanner('first-order', 'FIRST ORDER 20% OFF', 'Roboto Condensed', 30, 420, {
        fontStyle: 'bold',
        fill: '#ef4444',
        previewScale: 1.65,
      }),
      marketingBanner('welcome-back', 'WELCOME BACK', 'Playfair Display', 38, 360, {
        fontStyle: 'italic',
        fill: '#f8558e',
        previewScale: 1.8,
      }),
    ],
  },
  {
    id: 'announcements',
    title: 'Announcements',
    description: 'Openings, hiring, and business updates',
    templates: [
      marketingBanner('now-open', 'NOW OPEN', 'Bebas Neue', 58, 280, {
        fill: '#22c55e',
        letterSpacing: 3,
      }),
      marketingBanner('were-hiring', "WE'RE HIRING", 'Anton', 50, 340, {
        fill: '#2563eb',
      }),
      marketingBanner('under-renovation', 'REOPENING SOON', 'Montserrat', 38, 380, {
        fontStyle: 'bold',
        fill: '#f97316',
        previewScale: 1.75,
      }),
      marketingBanner('thank-you', 'THANK YOU', 'GreatVibes', 48, 280, {
        fill: '#f8558e',
        previewScale: 1.8,
      }),
      marketingBanner('celebrating', 'CELEBRATING 10 YEARS', 'DM Serif Display', 34, 420, {
        themeAdaptiveFill: true,
        previewScale: 1.7,
        keywords: ['anniversary'],
      }),
      marketingBanner('new-location', 'NEW LOCATION', 'League Spartan', 44, 360, {
        fontStyle: 'bold',
        fill: '#14b8a6',
      }),
      marketingBanner('proudly-local', 'PROUDLY LOCAL', 'Roboto Condensed', 34, 360, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.7,
      }),
      marketingBanner('award-winning', 'AWARD WINNING', 'Archivo Narrow', 32, 380, {
        fontStyle: 'bold',
        letterSpacing: 3,
        fill: '#fbbf24',
        previewScale: 1.75,
      }),
    ],
  },
  {
    id: 'fashion-beauty',
    title: 'Fashion & beauty',
    description: 'Style drops, salon, and beauty promos',
    templates: [
      marketingBanner('new-collection', 'NEW COLLECTION', 'Bodoni Moda', 44, 400, {
        letterSpacing: 4,
        fill: '#111111',
        previewScale: 1.85,
      }),
      marketingBanner('trend-alert', 'TREND ALERT', 'Bebas Neue', 52, 340, {
        fill: '#f8558e',
        letterSpacing: 2,
      }),
      marketingBanner('beauty-sale', 'BEAUTY SALE', 'Anton', 50, 360, {
        fill: '#ec4899',
      }),
      marketingBanner('skincare', 'SKINCARE ESSENTIALS', 'Cormorant Garamond', 32, 420, {
        fontStyle: 'italic',
        fill: '#fcd34d',
        previewScale: 1.65,
      }),
      marketingBanner('salon', 'BOOK YOUR GLOW', 'Parisienne', 40, 360, {
        fill: '#d63384',
        previewScale: 1.75,
      }),
      marketingBanner('accessories', 'ACCESSORIES', 'Oswald', 50, 320, {
        letterSpacing: 6,
        fill: '#ffffff',
      }),
      marketingBanner('runway', 'RUNWAY EDIT', 'Playfair Display', 40, 360, {
        fontStyle: 'italic',
        fill: '#ffffff',
        stroke: '#111111',
        strokeWidth: 1,
      }),
      marketingBanner('self-care', 'SELF CARE SUNDAY', 'Satisfy', 38, 400, {
        fill: '#a855f7',
        previewScale: 1.7,
      }),
      marketingBanner('limited-edition', 'LIMITED EDITION', 'Archivo Black', 42, 400, {
        fill: '#fbbf24',
        letterSpacing: 2,
        previewScale: 1.85,
      }),
      marketingBanner('style-guide', 'STYLE GUIDE', 'Montserrat', 42, 320, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 4,
      }),
    ],
  },
  {
    id: 'fitness-wellness',
    title: 'Fitness & wellness',
    description: 'Gym, classes, and health promos',
    templates: [
      marketingBanner('join-gym', 'JOIN TODAY', 'Bebas Neue', 54, 300, {
        fill: '#ef4444',
        letterSpacing: 2,
      }),
      marketingBanner('free-class', 'FIRST CLASS FREE', 'Barlow Condensed', 36, 400, {
        fontStyle: 'bold',
        fill: '#22c55e',
        previewScale: 1.75,
      }),
      marketingBanner('transform', 'TRANSFORM YOUR BODY', 'Oswald', 40, 400, {
        letterSpacing: 2,
        fill: '#ffffff',
        previewScale: 1.8,
      }),
      marketingBanner('wellness', 'WELLNESS WEEK', 'Montserrat', 40, 360, {
        fontStyle: 'bold',
        fill: '#14b8a6',
      }),
      marketingBanner('yoga', 'YOGA RETREAT', 'PetitFormalScript', 36, 360, {
        fill: '#6366f1',
        previewScale: 1.7,
      }),
      marketingBanner('nutrition', 'FUEL YOUR GOALS', 'Roboto Condensed', 32, 400, {
        fontStyle: 'bold',
        fill: '#f97316',
        previewScale: 1.7,
      }),
      marketingBanner('personal-training', 'PERSONAL TRAINING', 'League Spartan', 36, 400, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        previewScale: 1.75,
      }),
      marketingBanner('health-first', 'HEALTH FIRST', 'Anton', 50, 340, {
        fill: '#16a34a',
      }),
    ],
  },
  {
    id: 'travel-hospitality',
    title: 'Travel & hospitality',
    description: 'Stays, getaways, and booking CTAs',
    templates: [
      marketingBanner('book-stay', 'BOOK YOUR STAY', 'Playfair Display', 38, 400, {
        fontStyle: 'italic',
        themeAdaptiveFill: true,
        previewScale: 1.8,
      }),
      marketingBanner('weekend-getaway', 'WEEKEND GETAWAY', 'GreatVibes', 42, 400, {
        fill: '#0ea5e9',
        previewScale: 1.75,
      }),
      marketingBanner('luxury-escape', 'LUXURY ESCAPE', 'Cormorant Garamond', 40, 400, {
        fontStyle: 'italic',
        fill: '#fbbf24',
        previewScale: 1.8,
      }),
      marketingBanner('staycation', 'STAYCATION DEALS', 'Montserrat', 36, 400, {
        fontStyle: 'bold',
        fill: '#f8558e',
        previewScale: 1.75,
      }),
      marketingBanner('early-checkin', 'FREE EARLY CHECK-IN', 'Roboto Condensed', 28, 420, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.6,
      }),
      marketingBanner('adventure', 'ADVENTURE AWAITS', 'Bebas Neue', 50, 380, {
        fill: '#22c55e',
        letterSpacing: 2,
      }),
      marketingBanner('honeymoon', 'HONEYMOON PACKAGE', 'Parisienne', 36, 400, {
        fill: '#ec4899',
        previewScale: 1.7,
      }),
      marketingBanner('rooms-left', 'ONLY 2 ROOMS LEFT', 'Archivo Black', 36, 400, {
        fill: '#ef4444',
        previewScale: 1.75,
        keywords: ['urgency', 'hotel'],
      }),
    ],
  },
  {
    id: 'banner-combos',
    title: 'Banner combos',
    description: 'Headline + subline stacks for hero banners',
    templates: [
      marketingBanner('combo-mega', 'MEGA SALE\nUP TO 80% OFF', 'Bebas Neue', 52, 400, {
        lineHeight: 1.1,
        height: 90,
        fill: '#ef4444',
        letterSpacing: 2,
        previewScale: 1.9,
      }),
      marketingBanner('combo-flash', 'FLASH DEAL\nENDS SOON', 'Anton', 46, 380, {
        lineHeight: 1.12,
        height: 85,
        fill: '#fbbf24',
        previewScale: 1.85,
      }),
      marketingBanner('combo-brand', 'YOUR BRAND\nSHOP THE SALE', 'Montserrat', 34, 400, {
        fontStyle: 'bold',
        lineHeight: 1.15,
        height: 78,
        themeAdaptiveFill: true,
        previewScale: 1.55,
      }),
      marketingBanner('combo-launch', 'NEW LAUNCH\nLIMITED TIME', 'Oswald', 40, 400, {
        fontStyle: 'bold',
        lineHeight: 1.12,
        height: 80,
        fill: '#f8558e',
        letterSpacing: 2,
        previewScale: 1.7,
      }),
      marketingBanner('combo-free-ship', 'FREE SHIPPING\nON ORDERS $50+', 'Barlow Condensed', 32, 420, {
        fontStyle: 'bold',
        lineHeight: 1.15,
        height: 76,
        fill: '#22c55e',
        previewScale: 1.6,
      }),
      marketingBanner('combo-vip', 'VIP EARLY ACCESS\nSHOP FIRST', 'League Spartan', 30, 400, {
        fontStyle: 'bold',
        lineHeight: 1.15,
        height: 72,
        fill: '#a855f7',
        previewScale: 1.55,
      }),
    ],
  },
  {
    id: 'more-tags',
    title: 'More tags & stickers',
    description: 'Extra corner badges and promo stickers',
    templates: [
      retailTag('tag-must-have', 'MUST HAVE', 'Anton', 22, 180, { fill: '#f8558e' }),
      retailTag('tag-just-in', 'JUST IN', 'Bebas Neue', 24, 140, { fill: '#22c55e' }),
      retailTag('tag-save', 'SAVE', 'Archivo Black', 28, 120, { fill: '#fbbf24' }),
      retailTag('tag-deal', 'DEAL', 'Anton', 26, 120, { fill: '#ef4444' }),
      retailTag('tag-off', 'OFF', 'Bebas Neue', 30, 100, { fill: '#ffffff' }),
      retailTag('tag-pickup', 'PICKUP', 'Roboto Condensed', 18, 160, { fill: '#0ea5e9', previewScale: 2.8 }),
      retailTag('tag-delivery', 'DELIVERY', 'Barlow Condensed', 16, 180, {
        fill: '#14b8a6',
        previewScale: 2.85,
      }),
      retailTag('tag-guarantee', 'GUARANTEE', 'Montserrat', 16, 200, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 2.85,
      }),
      retailTag('tag-certified', 'CERTIFIED', 'League Spartan', 16, 200, {
        fill: '#2563eb',
        previewScale: 2.85,
      }),
      retailTag('tag-made-in', 'MADE IN USA', 'Roboto Condensed', 14, 220, {
        fill: '#1d4ed8',
        previewScale: 2.9,
      }),
      retailTag('tag-cruelty-free', 'CRUELTY FREE', 'Outfit', 14, 220, {
        fontStyle: 'bold',
        fill: '#15803d',
        previewScale: 2.9,
      }),
      retailTag('tag-bestseller-sm', '#1 BESTSELLER', 'Bebas Neue', 18, 220, {
        fill: '#fbbf24',
        previewScale: 2.8,
        keywords: ['amazon', 'rank'],
      }),
      retailTag('tag-low-stock', 'LOW STOCK', 'Anton', 22, 180, { fill: '#ef4444' }),
      retailTag('tag-price-cut', 'PRICE CUT', 'Oswald', 18, 200, { fill: '#f97316', previewScale: 2.75 }),
      retailTag('tag-warranty', '2YR WARRANTY', 'Roboto Condensed', 14, 220, {
        themeAdaptiveFill: true,
        previewScale: 2.9,
      }),
    ],
  },
  {
    id: 'more-offers',
    title: 'More offers',
    description: 'Extra discount lines and promo hooks',
    templates: [
      marketingBanner('off-15', '15% OFF', 'Bebas Neue', 56, 260, { fill: '#f8558e' }),
      marketingBanner('off-25', '25% OFF', 'Anton', 54, 280, { fill: '#f97316' }),
      marketingBanner('off-35', '35% OFF', 'Archivo Black', 52, 300, { fill: '#fbbf24' }),
      marketingBanner('off-60', '60% OFF', 'Bebas Neue', 54, 280, {
        fill: '#ffffff',
        stroke: '#dc2626',
        strokeWidth: 2,
      }),
      marketingBanner('off-70', '70% OFF', 'Lilita One', 50, 280, { fill: '#ef4444' }),
      marketingBanner('buy2save', 'BUY 2 SAVE MORE', 'Barlow Condensed', 38, 400, {
        fontStyle: 'bold',
        fill: '#7c3aed',
        previewScale: 1.75,
      }),
      marketingBanner('spend-save', 'SPEND $100 SAVE $20', 'Roboto Condensed', 28, 420, {
        fontStyle: 'bold',
        fill: '#16a34a',
        previewScale: 1.6,
      }),
      marketingBanner('student-discount', 'STUDENT DISCOUNT', 'Montserrat', 34, 400, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        previewScale: 1.7,
      }),
      marketingBanner('senior-discount', 'SENIOR DISCOUNT', 'Montserrat', 34, 400, {
        fontStyle: 'bold',
        fill: '#6366f1',
        previewScale: 1.7,
      }),
      marketingBanner('clearance-70', 'CLEARANCE 70% OFF', 'Anton', 44, 400, {
        fill: '#dc2626',
        previewScale: 1.9,
      }),
      marketingBanner('intro-offer', 'INTRO OFFER', 'Bebas Neue', 52, 320, {
        fill: '#22c55e',
        letterSpacing: 2,
      }),
      marketingBanner('gift-with', 'FREE GIFT WITH PURCHASE', 'Roboto Condensed', 26, 420, {
        fontStyle: 'bold',
        fill: '#a855f7',
        previewScale: 1.65,
      }),
    ],
  },
];
